/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import FormSection from './FormSection';
import { injectTestId } from '@cvent/nucleus-test-automation';

/**
 * Forms have sections, sections have rows, rows have columns.
 */
export default class Form extends React.Component {
  static propTypes = {
    // Form-specific
    /** Elements to be rendered in Form. `Form.Section` elements will be cloned and modified */
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]).isRequired,
    /** Screen reader label text (`aria-label` on web) */
    accessibilityLabel: PropTypes.string,
    /** Type of element to render. */
    element: PropTypes.oneOf(['form', 'div']),
    // i18n
    /** Right to left language preference */
    isRtl: PropTypes.bool,
    /** Should it stack */
    stack: PropTypes.bool,
    // testing/automation
    /** Target outer container in e2e tests (`data-cvent-id` on web) */
    testID: PropTypes.string
  };

  static defaultProps = {
    element: 'form'
  };

  static Section = FormSection;

  render() {
    const {
      accessibilityLabel,
      element,
      isRtl,
      stack,
      testID,
      css,
      className,
      style,
      forwardedRef,
      ...propDrop
    } = this.props;

    const Element = element;

    return (
      <Element
        ref={forwardedRef}
        onSubmit={this.handleSubmit}
        onKeyPress={this.watchEnterClick}
        onClick={this.watchSubmitClick}
        {...propDrop}
        {...injectTestId(testID)}
        css={getContainerStyles(this.props)}
        aria-label={this.props.accessibilityLabel}
      >
        {this.renderSections(this.props)}
      </Element>
    );
  }

  /**
   * This function is meant to be an event listened to on the form element. Specifically when the form is element="div"
   *
   * The idea is to mimic the behaviour of a real form when the element is a div. This follows the conditions around an
   * implicit form submission (https://www.w3.org/TR/html52/sec-forms.html#implicit-submission), where the user hits the
   * enter key while focusing an input element.
   *
   * @param {KeyboardEvent} event The synthetic keyboard event from onKeyPress
   */
  watchEnterClick = event => {
    const { key, target, currentTarget } = event;
    const { onSubmit, element } = this.props;

    // Find a submit button that is a child of the form element.
    const submitButton = currentTarget.querySelector(
      'input[type="submit"], button[type="submit"]'
    );

    // Count the number of fields that block implicit submission.
    const implicitSubmissionInputCount = currentTarget.querySelectorAll(
      'input:not([type]),input[type="text"],input[type="search"],input[type="url"],input[type="tel"],input[type="email"],input[type="password"],input[type="datetime-local"],input[type="date"],input[type="month"],input[type="week"],input[type="time"],input[type="number"]'
    ).length;

    if (
      // Only apply logic if the form is a div type form
      element === 'div' &&
      // Don't do anything if the onSubmit is not a function
      typeof onSubmit === 'function' &&
      // The enter key is the trigger
      key === 'Enter' &&
      // Focused element must be an input
      target.tagName.toLowerCase() === 'input' &&
      // If the form doesn't have a submit button the number of implicit submission blockers must be 1 or less
      (submitButton || implicitSubmissionInputCount <= 1) &&
      // Don't submit if the submit button is disabled
      !submitButton?.disabled &&
      // Ignore if focused input is the submit button. It will be handled by the onClick
      (!target.getAttribute('type') ||
        target.getAttribute('type').toLowerCase() !== 'submit')
    ) {
      // Pass the event so stopping propagation and preventing default is an option in the onSubmit function
      onSubmit(event);
    }
  };

  /**
   * This function is meant to listen to clicks on the form element. Specifically when the form is element="div"
   *
   * We check to see if the target element is a submit button and if it is we can submit the div form.
   *
   * @param {ClickEvent} event The synthetic click event from onClick
   */
  watchSubmitClick = event => {
    const { target } = event;
    const { onSubmit, element } = this.props;

    if (
      element === 'div' &&
      typeof onSubmit === 'function' &&
      target.getAttribute('type') === 'submit'
    ) {
      // Pass the event so stopping propagation and preventing default is an option in the onSubmit function
      onSubmit(event);
    }
  };

  renderSections = ({ children, isRtl, stack }) =>
    React.Children.map(children, element => {
      if (element && element.type && element.type.layoutId === 'FormSection') {
        return React.cloneElement(element, { isRtl, stack });
      }
      return element;
    });

  handleSubmit = event => event.preventDefault();
}

function getContainerStyles() {
  return {
    width: '100%',
    position: 'relative'
  };
}
